import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { Column, ColumnSubType } from '@scale/compass-shared/types';
import { useCompassCard } from '@scale/compass-ui/store';
import {
  Select,
  TextField,
  Heading,
  Text,
  Badge,
  TextArea,
  Flex,
  Button,
  Callout,
  Spinner,
  Icon,
  Code,
  DataTable,
  DropdownMenu,
  IconButton,
} from '@scale/scaleui-radix';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { createColumnHelper } from '@tanstack/react-table';
import type { ResponseBackfillContext } from '@scale/genai-compass-shared/cards/response_backfill';
import YamlDiffView from '@scale/compass-ui/components/YamlDiffView';
import apiFetch from 'react-scale/lib/api-fetch';
import {
  getBackfillableStepsForSection,
  coerceBackfillValue,
  validateBackfillOperation,
  isFullResponsePath,
  isSingleCriterionPath,
  type BackfillableStep,
  type BackfillableItem,
} from '@scale/task-builder-shared';
import type { TextCollectionField } from '@scale/shared/types/params/textcollection';
import { InteractionStepType, type InteractionStepParams } from '@scale/shared/types/chat';

/**
 * Deep clone an object
 */
const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj));

/**
 * Set a nested value in an object using a dot-notation path
 * Supports array indexing like "turns[0].prompt"
 */
const setNestedValue = (obj: any, path: string, value: any): void => {
  const parts = path.split('.').flatMap(part => {
    // Handle array notation like "turns[0]"
    const match = part.match(/^([^[]+)\[(\d+)\]$/);
    if (match) {
      return [match[1], parseInt(match[2], 10)];
    }
    return [part];
  });

  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (current[part] === undefined) {
      current[part] = typeof parts[i + 1] === 'number' ? [] : {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
};

/**
 * Check if a string is a valid non-negative integer (or empty, which defaults to 0)
 */
const isValidTurnIndex = (value: string): boolean => {
  if (!value || value.trim() === '') return true; // Empty is valid, defaults to 0
  const num = parseInt(value, 10);
  return !isNaN(num) && num >= 0 && num.toString() === value.trim();
};

/**
 * Interpolate {{variable}} placeholders in a template string with values from a data row
 */
const interpolateTemplate = (template: string, rowData: Record<string, any>): string => {
  return template.replace(/\{\{(\w+)\}\}/g, (match, variableName) => {
    const value = rowData[variableName];
    if (value === undefined || value === null) {
      return match; // Keep the placeholder if value not found
    }
    // Convert value to string
    return typeof value === 'object' ? JSON.stringify(value) : String(value);
  });
};

/**
 * Validates and coerces a value for preview.
 * Returns { error } if validation fails, or { coercedValue } if successful.
 */
const validateAndCoerceForPreview = (
  original: any,
  params: any,
  section: string,
  turnIndex: number,
  stepId: string,
  field: string,
  interpolatedValue: string,
): { error: string } | { coercedValue: unknown } => {
  const sectionConfig = SECTION_CONFIG.find(s => s.value === section);

  // Get the step response to find the step type
  const sectionResponse = sectionConfig?.requiresIndex
    ? original[section]?.[turnIndex]
    : original[section];
  const stepResponse = sectionResponse?.[stepId];

  if (!stepResponse?.type) {
    return { error: `Step '${stepId}' not found in section '${section}'.` };
  }

  // Get taxonomy steps for field definition lookup
  const taxSteps = sectionConfig?.requiresIndex ? params?.turn : params?.[section];

  // Find field definition for form fields (needed for type coercion)
  const fieldDefinition = findFieldDefinition(taxSteps || null, stepId, field);

  // Coerce the value to the expected type
  const coercedValue = coerceBackfillValue(
    stepResponse.type,
    field,
    interpolatedValue,
    fieldDefinition,
  );

  // Validate the backfill operation
  const mappedSection = section === 'turn' ? 'turns' : section;
  const validationResult = validateBackfillOperation(
    {
      section: mappedSection as 'before' | 'turns' | 'after',
      stepId,
      fieldPath: field,
      newValue: coercedValue,
      turnIndex: sectionConfig?.requiresIndex ? turnIndex : undefined,
    },
    {
      response: original,
      params: params || {},
      fullParams: {
        dataConnections: params?.dataConnections,
      },
    },
  );

  if (!validationResult.isValid) {
    return { error: `Validation failed: ${validationResult.error}` };
  }

  return { coercedValue };
};

/**
 * Finds the field definition for a form field from taxonomy steps.
 * Returns undefined for content object paths (like output.content).
 *
 * Handles various path patterns:
 * - output.field_id (TextCollection)
 * - output[0].output.field_id (TextCollectionPerResponse)
 * - output.criteria[0].annotations.field_id (RubricCriteriaBuilder)
 */
const findFieldDefinition = (
  taxonomySteps: InteractionStepParams[] | null,
  stepId: string,
  fieldPath: string,
): TextCollectionField | undefined => {
  if (!fieldPath.startsWith('output')) {
    return undefined;
  }

  // Extract field_id from the path - it's the last segment after the final '.'
  const pathParts = fieldPath.split('.');
  const fieldId = pathParts[pathParts.length - 1];

  // Skip known content object paths - these aren't form fields
  const contentObjectPaths = [
    'content',
    'originalContent',
    'responseIdx',
    'preferenceLikert',
    'rankedCandidates',
  ];
  if (contentObjectPaths.includes(fieldId)) {
    return undefined;
  }

  // Skip array index patterns like [0] or patterns that look like IDs (response-0, crit-1)
  if (/^\[\d+\]$/.test(fieldId) || /^(response|crit)-/.test(fieldId)) {
    return undefined;
  }

  if (!taxonomySteps) {
    return undefined;
  }

  const step = taxonomySteps.find(s => s.id === stepId);
  if (!step?.params || !('fields' in step.params)) {
    return undefined;
  }

  const fields = (step.params as { fields: TextCollectionField[] }).fields;
  return fields.find(f => f.field_id === fieldId);
};

/**
 * Configuration for available sections
 */
type SectionConfig = {
  value: string;
  label: string;
  requiresIndex: boolean; // Whether this section needs a turn index
};

// Available sections and their configurations
const SECTION_CONFIG: SectionConfig[] = [
  { value: 'before', label: 'before', requiresIndex: false },
  { value: 'turns', label: 'turns', requiresIndex: true },
  { value: 'after', label: 'after', requiresIndex: false },
];

/**
 * Preview table row data structure
 */
interface PreviewRowData {
  taskId: string;
  oldValue: string;
  newValue: string;
  originalJson: any;
  updatedJson: any;
  error?: string;
}

/**
 * Get the value at a nested path in an object
 */
const getNestedValue = (obj: any, path: string): any => {
  const parts = path.split('.').flatMap(part => {
    const match = part.match(/^([^[]+)\[(\d+)\]$/);
    if (match) {
      return [match[1], parseInt(match[2], 10)];
    }
    return [part];
  });

  let current = obj;
  for (const part of parts) {
    if (current === undefined || current === null) return undefined;
    current = current[part];
  }
  return current;
};

/**
 * Truncate a string for display
 */
const truncateValue = (value: any, maxLength: number = 50): string => {
  if (value === undefined || value === null) return '(empty)';
  const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};

// Table column helper
const columnHelper = createColumnHelper<PreviewRowData>();

// Table columns definition
const previewTableColumns = [
  columnHelper.accessor('taskId', {
    id: 'taskId',
    header: 'Task ID',
    size: 220,
    cell: function TaskIdCell(info) {
      return (
        <Code size="1" className="text-xs">
          {info.getValue()}
        </Code>
      );
    },
  }),
  columnHelper.accessor('oldValue', {
    id: 'oldValue',
    header: 'Current Value',
    size: 300,
    cell: function CurrentValueCell(info) {
      return (
        <Text size="1" className="font-mono">
          {info.getValue() || '(empty)'}
        </Text>
      );
    },
  }),
  columnHelper.accessor('newValue', {
    id: 'newValue',
    header: 'New Value',
    size: 300,
    cell: function NewValueCell(info) {
      const row = info.row.original;
      if (row.error) {
        return (
          <Text size="1" color="red">
            {row.error}
          </Text>
        );
      }
      return (
        <Text size="1" className="font-mono">
          {info.getValue()}
        </Text>
      );
    },
  }),
];

// eslint-disable-next-line complexity
const ResponseBackfillCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const { card, updateCardContext, updateCardCanSubmit } =
    useCompassCard<ResponseBackfillContext>(index);
  const context = card.defaultContext;
  const [showPreview, setShowPreview] = useState(false);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [taskResponse, setTaskResponse] = useState<any>(null);
  const [taskParams, setTaskParams] = useState<any>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  // Preview table state
  const [previewTableData, setPreviewTableData] = useState<PreviewRowData[]>([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);
  const [previewIsStale, setPreviewIsStale] = useState(true);
  const { retrievedOutput } = useCompassCard(index - 1);

  // Get column names from the previous card's output (excluding the Task ID column)
  const columnNames = useMemo(() => {
    const schema = retrievedOutput?.response?.schema as Column[] | undefined;
    if (!schema) return [];
    return schema
      .filter((col: Column) => col.subType !== ColumnSubType.TASK_ID)
      .map((col: Column) => col.header);
  }, [retrievedOutput]);

  // Get all rows of data
  const allRowsData = useMemo(() => {
    return retrievedOutput?.response?.data || [];
  }, [retrievedOutput]);

  // Helper to invalidate preview - updates both state and canSubmit together
  const invalidatePreview = useCallback(() => {
    setPreviewIsStale(true);
    updateCardCanSubmit(false);
  }, [updateCardCanSubmit]);

  // Get the first row of data (for step/field loading)
  const firstRowData = useMemo(() => {
    return allRowsData[0] || null;
  }, [allRowsData]);

  // Find the taskId column name
  const taskIdColumnName = useMemo(() => {
    const schema = retrievedOutput?.response?.schema as Column[] | undefined;
    if (!schema) return null;
    const taskIdCol = schema.find((col: Column) => col.subType === ColumnSubType.TASK_ID);
    return taskIdCol?.header || 'taskId';
  }, [retrievedOutput]);

  // Get the taskId from the first row for populating dropdowns
  const taskId = useMemo(() => {
    if (!firstRowData || !taskIdColumnName) return null;
    return firstRowData[taskIdColumnName];
  }, [firstRowData, taskIdColumnName]);

  // Fetch the task response and params when taskId changes
  // Uses the /response endpoint which works for both pending and completed tasks
  useEffect(() => {
    if (!taskId) {
      setTaskResponse(null);
      setTaskParams(null);
      setResponseError(null);
      return;
    }

    const fetchTaskData = async () => {
      setIsLoadingResponse(true);
      setResponseError(null);
      try {
        // Use the /response endpoint which queries Temporal for pending tasks
        const result = await apiFetch<{ response: any; status: string; params: any }>(
          null,
          `corp-api/tasks/${taskId}/response`,
          { passError: true },
        );
        if (result?.response) {
          setTaskResponse(result.response);
          setTaskParams(result.params || null);
        } else {
          setResponseError(`Task ${taskId} does not have a response yet.`);
          setTaskResponse(null);
          setTaskParams(null);
        }
      } catch (err) {
        setResponseError(
          `Failed to fetch task: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
        setTaskResponse(null);
        setTaskParams(null);
      } finally {
        setIsLoadingResponse(false);
      }
    };

    fetchTaskData();
  }, [taskId]);

  // Get current section config
  const currentSectionConfig = useMemo(() => {
    return SECTION_CONFIG.find(s => s.value === (context.section || 'turns')) || SECTION_CONFIG[1];
  }, [context.section]);

  // Whether the current section requires a turn index
  const requiresTurnIndex = currentSectionConfig.requiresIndex;

  // Get available turn indices from the task response
  const availableTurnIndices = useMemo(() => {
    if (!taskResponse?.turns || !Array.isArray(taskResponse.turns)) return [];
    return taskResponse.turns.map((_: any, index: number) => ({
      value: String(index),
      label: `Turn ${index}`,
    }));
  }, [taskResponse]);

  // Check if a valid turn is selected (for enabling step dropdown)
  const hasValidTurnSelected = useMemo(() => {
    if (!requiresTurnIndex) return true; // Not required for before/after sections
    if (availableTurnIndices.length === 0) return false; // No turns available
    const turnIndex = context.turnIndex || '';
    return availableTurnIndices.some(
      (t: { value: string; label: string }) => t.value === turnIndex,
    );
  }, [requiresTurnIndex, availableTurnIndices, context.turnIndex]);

  // Get the section response and taxonomy steps based on current section and turn index
  const { sectionResponse, taxonomySteps } = useMemo(() => {
    if (!taskResponse) return { sectionResponse: null, taxonomySteps: null };

    const section = context.section || 'turns';
    const turnIndex = parseInt(context.turnIndex || '0', 10);

    let sectionResp: any;
    let taxSteps: any[] | null = null;

    if (currentSectionConfig.requiresIndex) {
      // For turns, access the array element
      sectionResp = taskResponse[section]?.[turnIndex];
      // Taxonomy for turns is in task.params.turn (singular)
      taxSteps = taskParams?.turn || null;
    } else {
      // For before/after, access directly
      sectionResp = taskResponse[section];
      taxSteps = taskParams?.[section] || null;
    }

    return { sectionResponse: sectionResp, taxonomySteps: taxSteps };
  }, [taskResponse, taskParams, context.section, context.turnIndex, currentSectionConfig]);

  // Use the backfill-fields utility to get backfillable steps and their items
  const backfillableSteps = useMemo((): BackfillableStep[] => {
    if (!sectionResponse) return [];
    return getBackfillableStepsForSection(sectionResponse, taxonomySteps || undefined);
  }, [sectionResponse, taxonomySteps]);

  // Get the list of steps with their backfillable status
  const stepsWithStatus = useMemo(() => {
    return backfillableSteps.map((step: BackfillableStep) => ({
      value: step.stepId,
      label: `${step.stepId} (${step.stepType})`,
      enabled: step.items.length > 0, // Only enabled if has backfillable items
    }));
  }, [backfillableSteps]);

  // Get the current step's backfillable items (fields)
  const currentStepItems = useMemo((): BackfillableItem[] => {
    const currentStep = backfillableSteps.find(
      (step: BackfillableStep) => step.stepId === context.stepId,
    );
    return currentStep?.items || [];
  }, [backfillableSteps, context.stepId]);

  // Get available fields for the current step (formatted for dropdown)
  // Only show top-level items, not individual subItems
  const availableFields = useMemo(() => {
    return currentStepItems.map((item: BackfillableItem) => ({
      value: item.path,
      label: item.label,
      hasSubItems: (item.subItems?.length ?? 0) > 0,
    }));
  }, [currentStepItems]);

  // Get the currently selected field item
  const selectedFieldItem = useMemo(() => {
    return currentStepItems.find((item: BackfillableItem) => item.path === context.field);
  }, [currentStepItems, context.field]);

  // Get sub-items for the selected field (if any)
  const currentFieldSubItems = useMemo(() => {
    if (!selectedFieldItem?.subItems) return [];
    return selectedFieldItem.subItems.map((sub: BackfillableItem) => ({
      value: sub.path,
      label: sub.label,
    }));
  }, [selectedFieldItem]);

  // The actual field path to use for backfill (considers subField selection)
  const effectiveFieldPath = useMemo(() => {
    // If there are subItems and a subField is selected, use the subField path
    if (currentFieldSubItems.length > 0 && context.subField) {
      return context.subField;
    }
    // Otherwise use the main field path
    return context.field;
  }, [currentFieldSubItems, context.subField, context.field]);

  // Check if the current field selection is an RCB full response path
  const isRCBFullResponseMode = useMemo(() => {
    const currentStep = backfillableSteps.find(s => s.stepId === context.stepId);
    if (!currentStep) return false;
    return (
      currentStep.stepType === InteractionStepType.RubricCriteriaBuilder &&
      isFullResponsePath(currentStep.stepType, effectiveFieldPath)
    );
  }, [backfillableSteps, context.stepId, effectiveFieldPath]);

  // Check if the current field selection is a single criterion path
  const isSingleCriterionMode = useMemo(() => {
    const currentStep = backfillableSteps.find(s => s.stepId === context.stepId);
    return currentStep ? isSingleCriterionPath(currentStep.stepType, effectiveFieldPath) : false;
  }, [backfillableSteps, context.stepId, effectiveFieldPath]);

  // Check if the current field selection is an RCR full response path
  const isRCRFullResponseMode = useMemo(() => {
    const currentStep = backfillableSteps.find(s => s.stepId === context.stepId);
    if (!currentStep) return false;
    return (
      currentStep.stepType === InteractionStepType.RubricCriteriaRating &&
      isFullResponsePath(currentStep.stepType, effectiveFieldPath)
    );
  }, [backfillableSteps, context.stepId, effectiveFieldPath]);

  // Check if the current field selection is an RCR per-response mode (all criteria for one response)
  // Path structure: output.responseRatings.{responseId} = 3 segments
  const isRCRPerResponseMode = useMemo(() => {
    const currentStep = backfillableSteps.find(s => s.stepId === context.stepId);
    if (!currentStep) return false;
    if (currentStep.stepType !== InteractionStepType.RubricCriteriaRating) return false;
    // Check for exactly 3 segments (response selected, but not a specific criterion)
    const segmentCount = effectiveFieldPath.split('.').length;
    return segmentCount === 3;
  }, [backfillableSteps, context.stepId, effectiveFieldPath]);

  // Check if the current field selection is an RCR single rating mode (specific criterion selected)
  // Path structure: output.responseRatings.{responseId}.{criterionId} = 4 segments
  const isRCRSingleRatingMode = useMemo(() => {
    const currentStep = backfillableSteps.find(s => s.stepId === context.stepId);
    if (!currentStep) return false;
    if (currentStep.stepType !== InteractionStepType.RubricCriteriaRating) return false;
    // Check for 4+ segments (response + criterion selected)
    const segmentCount = effectiveFieldPath.split('.').length;
    return segmentCount >= 4;
  }, [backfillableSteps, context.stepId, effectiveFieldPath]);

  // Ensure Run button starts disabled on mount (preview required before first run)
  useEffect(() => {
    updateCardCanSubmit(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
  }, []);

  const handleSectionChange = (value: string) => {
    const newSectionConfig = SECTION_CONFIG.find((s: SectionConfig) => s.value === value);
    // Reset step and field when section changes - they will be populated from task data
    updateCardContext({
      ...context,
      section: value,
      stepId: '', // Will be populated when task data loads
      field: '', // Will be populated when step is selected
      turnIndex: newSectionConfig?.requiresIndex ? context.turnIndex : '', // Clear turn index if not needed
    });
    invalidatePreview();
  };

  const handleStepIdChange = (value: string) => {
    // Find the first field for the new step
    const newStep = backfillableSteps.find((s: BackfillableStep) => s.stepId === value);
    const firstField = newStep?.items[0];
    updateCardContext({
      ...context,
      stepId: value,
      field: firstField?.path || '',
    });
    invalidatePreview();
  };

  const handleFieldChange = (value: string) => {
    // Find the item to check if it has subItems
    const item = currentStepItems.find((i: BackfillableItem) => i.path === value);
    const firstSubItem = item?.subItems?.[0];
    updateCardContext({
      ...context,
      field: value,
      // Auto-select first subItem if available, otherwise clear
      subField: firstSubItem?.path || '',
    });
    invalidatePreview();
  };

  const handleSubFieldChange = (value: string) => {
    updateCardContext({ ...context, subField: value });
    invalidatePreview();
  };

  const handleTurnIndexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numeric input
    const value = e.target.value.replace(/[^0-9]/g, '');
    // Reset step and field when turn changes
    updateCardContext({
      ...context,
      turnIndex: value,
      stepId: '',
      field: '',
    });
    invalidatePreview();
  };

  const handleTurnIndexDropdownSelect = (value: string) => {
    // Reset step and field when turn changes
    updateCardContext({
      ...context,
      turnIndex: value,
      stepId: '',
      field: '',
    });
    invalidatePreview();
  };

  const handleNewValueTemplateChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateCardContext({ ...context, newValueTemplate: e.target.value });
    invalidatePreview();
  };

  const insertVariable = (variable: string) => {
    const newValue = (context.newValueTemplate || '') + `{{${variable}}}`;
    updateCardContext({ ...context, newValueTemplate: newValue });
    invalidatePreview();
  };

  const handlePreview = useCallback(async () => {
    setPreviewError(null);
    setShowPreview(false);
    setPreviewTableData([]);
    setSelectedRowIndex(0);

    const section = context.section || 'turns';
    const stepId = context.stepId || '';
    // Use effectiveFieldPath which considers subField selection
    const field = effectiveFieldPath || '';

    // Validate preconditions for multi-row processing
    if (allRowsData.length === 0) {
      setPreviewError('No data available. Add a data source card first.');
      return;
    }

    if (!taskIdColumnName) {
      setPreviewError('No taskId column found in the data.');
      return;
    }

    if (!stepId || !field) {
      setPreviewError('Please select a step and field to update.');
      return;
    }

    // Validate turn index if section requires it
    const sectionRequiresIndex = SECTION_CONFIG.find(s => s.value === section)?.requiresIndex;
    if (sectionRequiresIndex && !isValidTurnIndex(context.turnIndex || '')) {
      setPreviewError('Turn index must be a non-negative integer.');
      return;
    }

    setIsLoadingPreview(true);

    try {
      const turnIndex = parseInt(context.turnIndex || '0', 10);
      const sectionConfig = SECTION_CONFIG.find((s: SectionConfig) => s.value === section);
      const path = sectionConfig?.requiresIndex
        ? `${section}[${turnIndex}].${stepId}.${field}`
        : `${section}.${stepId}.${field}`;

      // Process all rows in parallel with validation
      const previewRows: PreviewRowData[] = await Promise.all(
        allRowsData.map(async (rowData: Record<string, any>) => {
          const rowTaskId = rowData[taskIdColumnName];
          if (!rowTaskId) {
            return {
              taskId: '(missing)',
              oldValue: '',
              newValue: '',
              originalJson: null,
              updatedJson: null,
              error: 'No taskId in row',
            };
          }

          try {
            const result = await apiFetch<{ response: any; status: string; params: any }>(
              null,
              `corp-api/tasks/${rowTaskId}/response`,
              { passError: true },
            );

            if (!result?.response) {
              return {
                taskId: rowTaskId,
                oldValue: '',
                newValue: '',
                originalJson: null,
                updatedJson: null,
                error: 'No response yet',
              };
            }

            // Skip completed tasks - they are not eligible for backfill
            if (result.status === 'completed') {
              return {
                taskId: rowTaskId,
                oldValue: '',
                newValue: '',
                originalJson: null,
                updatedJson: null,
                error: 'Task is completed (not eligible for backfill)',
              };
            }

            const original = result.response;

            // Get the old value at the path
            const oldValue = getNestedValue(original, path);

            // Interpolate the template with actual values from this row
            const interpolatedValue = interpolateTemplate(context.newValueTemplate || '', rowData);

            // Validate and coerce the value for this task
            const validationOrValue = validateAndCoerceForPreview(
              original,
              result.params,
              section,
              turnIndex,
              stepId,
              field,
              interpolatedValue,
            );

            if ('error' in validationOrValue) {
              return {
                taskId: rowTaskId,
                oldValue: truncateValue(oldValue),
                newValue: truncateValue(interpolatedValue),
                originalJson: original,
                updatedJson: null,
                error: validationOrValue.error,
              };
            }

            // Use the coerced value in the preview (matches what backend will store)
            const updated = deepClone(original);
            setNestedValue(updated, path, validationOrValue.coercedValue);

            return {
              taskId: rowTaskId,
              oldValue: truncateValue(oldValue),
              newValue: truncateValue(validationOrValue.coercedValue),
              originalJson: original,
              updatedJson: updated,
            };
          } catch (err) {
            return {
              taskId: rowTaskId,
              oldValue: '',
              newValue: '',
              originalJson: null,
              updatedJson: null,
              error: err instanceof Error ? err.message : 'Failed to fetch',
            };
          }
        }),
      );

      setPreviewTableData(previewRows);
      setSelectedRowIndex(0); // Select first row by default
      setShowPreview(true);
      setPreviewIsStale(false);
      updateCardCanSubmit(true); // Enable Run button after successful preview
    } catch (err) {
      setPreviewError(
        `Failed to generate preview: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
    } finally {
      setIsLoadingPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateCardCanSubmit identity doesn't matter
  }, [context, allRowsData, taskIdColumnName, effectiveFieldPath]);

  // Get the currently selected row's data for the diff viewer
  const selectedRowData = useMemo(() => {
    if (previewTableData.length === 0) return null;
    return previewTableData[selectedRowIndex] || null;
  }, [previewTableData, selectedRowIndex]);

  // Handle row selection in the preview table
  const handleRowSelectionChange = useCallback(
    (
      newSelection:
        | Record<string, boolean>
        | ((old: Record<string, boolean>) => Record<string, boolean>),
    ) => {
      const currentSelection = { [selectedRowIndex]: true };
      const finalSelection =
        typeof newSelection === 'function' ? newSelection(currentSelection) : newSelection;

      const newSelectedIndex = Object.entries(finalSelection).find(
        ([, isSelected]) => isSelected,
      )?.[0];
      if (newSelectedIndex !== undefined) {
        setSelectedRowIndex(parseInt(newSelectedIndex, 10));
      }
    },
    [selectedRowIndex],
  );

  // Current row selection state for the table
  const currentRowSelection = useMemo(() => {
    return { [selectedRowIndex]: true };
  }, [selectedRowIndex]);

  return (
    <div className="flex flex-col gap-6">
      {/* Task field to update section */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Heading size="3" weight="medium">
            Task field to update
          </Heading>
          <Text size="2" color="gray">
            Select the existing field you want to update in the task.
          </Text>
        </div>

        <div className="flex flex-row flex-wrap gap-4">
          <div className="flex flex-col gap-2">
            <Text size="2" weight="medium">
              Section
            </Text>
            <Select.Root value={context.section || 'turns'} onValueChange={handleSectionChange}>
              <Select.Trigger className="w-28" />
              <Select.Content>
                {SECTION_CONFIG.map(section => (
                  <Select.Item key={section.value} value={section.value}>
                    {section.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </div>

          {requiresTurnIndex && (
            <div className="flex flex-col gap-2">
              <Text size="2" weight="medium">
                Turn Index
              </Text>
              <TextField.Root
                className="w-20"
                value={context.turnIndex || ''}
                onChange={handleTurnIndexInputChange}
              >
                <TextField.Slot side="right">
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger>
                      <IconButton
                        variant="ghost"
                        size="1"
                        disabled={isLoadingResponse || availableTurnIndices.length === 0}
                      >
                        <Icon icon={faChevronDown} size="1" label="Select turn" />
                      </IconButton>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content>
                      {availableTurnIndices.map((turn: { value: string; label: string }) => (
                        <DropdownMenu.Item
                          key={turn.value}
                          onSelect={() => handleTurnIndexDropdownSelect(turn.value)}
                        >
                          {turn.value}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Root>
                </TextField.Slot>
              </TextField.Root>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Text size="2" weight="medium">
              Step ID
            </Text>
            <Select.Root
              value={context.stepId || ''}
              onValueChange={handleStepIdChange}
              disabled={isLoadingResponse || !hasValidTurnSelected || stepsWithStatus.length === 0}
            >
              <Select.Trigger className="w-96" placeholder="Select a step..." />
              <Select.Content>
                {isLoadingResponse ? (
                  <Select.Item value="loading" disabled>
                    Loading...
                  </Select.Item>
                ) : stepsWithStatus.length > 0 ? (
                  stepsWithStatus.map(
                    (step: { value: string; label: string; enabled: boolean }) => (
                      <Select.Item key={step.value} value={step.value} disabled={!step.enabled}>
                        {step.label}
                      </Select.Item>
                    ),
                  )
                ) : (
                  <Select.Item value="__no_steps__" disabled>
                    No backfillable steps found
                  </Select.Item>
                )}
              </Select.Content>
            </Select.Root>
            {responseError && (
              <Text size="1" color="red">
                {responseError}
              </Text>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Text size="2" weight="medium">
              Field
            </Text>
            <Select.Root
              value={context.field || ''}
              onValueChange={handleFieldChange}
              disabled={availableFields.length === 0}
            >
              <Select.Trigger className="w-80" placeholder="Select a field..." />
              <Select.Content>
                {availableFields.length > 0 ? (
                  availableFields.map(
                    (field: { value: string; label: string; hasSubItems: boolean }) => (
                      <Select.Item key={field.value} value={field.value}>
                        {field.label}
                      </Select.Item>
                    ),
                  )
                ) : (
                  <Select.Item value="__no_fields__" disabled>
                    {context.stepId ? 'No backfillable fields' : 'Select a step first'}
                  </Select.Item>
                )}
              </Select.Content>
            </Select.Root>
          </div>

          {/* Sub-field dropdown - shown when field has subItems */}
          {currentFieldSubItems.length > 0 && (
            <div className="flex flex-col gap-2">
              <Text size="2" weight="medium">
                Sub-field
              </Text>
              <Select.Root value={context.subField || ''} onValueChange={handleSubFieldChange}>
                <Select.Trigger className="w-80" placeholder="Select a sub-field..." />
                <Select.Content>
                  {currentFieldSubItems.map((subField: { value: string; label: string }) => (
                    <Select.Item key={subField.value} value={subField.value}>
                      {subField.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </div>
          )}
        </div>
      </div>

      {/* New value section */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Heading size="3" weight="medium">
            New value
          </Heading>
          <Text size="2" color="gray">
            Input the new value that you want for this field
          </Text>
        </div>

        <div className="flex flex-col gap-3">
          {isRCBFullResponseMode && (
            <Callout.Root size="1" color="blue" className="mb-2">
              <Callout.Text>
                Enter a JSON array of criteria objects. Each criterion needs <Code>id</Code>{' '}
                (string) and <Code>title</Code> (string). Optional: <Code>weight</Code> (number) and{' '}
                <Code>annotations</Code> (object).
              </Callout.Text>
            </Callout.Root>
          )}
          {isSingleCriterionMode && (
            <Callout.Root size="1" color="blue" className="mb-2">
              <Callout.Text>
                Enter a JSON object for this criterion. Required: <Code>id</Code> (string) and{' '}
                <Code>title</Code> (string). Optional: <Code>weight</Code> (number) and{' '}
                <Code>annotations</Code> (object).
              </Callout.Text>
            </Callout.Root>
          )}
          {isRCRFullResponseMode && (
            <Callout.Root size="1" color="blue" className="mb-2">
              <Callout.Text>
                Enter a JSON object with ratings for all responses. Structure:{' '}
                <Code>
                  {
                    '{ "responseId": { "criterionId": { "id": "ratingId", "title": "...", "color": "..." } } }'
                  }
                </Code>
                . The <Code>id</Code> must match a valid rating option. Optional: <Code>score</Code>{' '}
                (number) and <Code>justification</Code> (string).
              </Callout.Text>
            </Callout.Root>
          )}
          {isRCRPerResponseMode && (
            <Callout.Root size="1" color="blue" className="mb-2">
              <Callout.Text>
                Enter a JSON object with ratings for all criteria in this response. Structure:{' '}
                <Code>
                  {'{ "criterionId": { "id": "ratingId", "title": "...", "color": "..." } }'}
                </Code>
                . The <Code>id</Code> must match a valid rating option. Optional: <Code>score</Code>{' '}
                (number) and <Code>justification</Code> (string).
              </Callout.Text>
            </Callout.Root>
          )}
          {isRCRSingleRatingMode && (
            <Callout.Root size="1" color="blue" className="mb-2">
              <Callout.Text>
                Enter a JSON object for this criterion rating. Structure:{' '}
                <Code>{'{ "id": "ratingId", "title": "...", "color": "..." }'}</Code>. The{' '}
                <Code>id</Code> must match a valid rating option. Optional: <Code>score</Code>{' '}
                (number) and <Code>justification</Code> (string).
              </Callout.Text>
            </Callout.Root>
          )}
          <Flex gap="2" wrap="wrap">
            {columnNames.length > 0 ? (
              columnNames.map((columnName: string) => (
                <Badge
                  key={columnName}
                  size="2"
                  color="accent"
                  className="cursor-pointer hover:opacity-80"
                  onClick={() => insertVariable(columnName)}
                >
                  {columnName}
                </Badge>
              ))
            ) : (
              <Text size="2" color="gray">
                No columns available. Add a data source card first.
              </Text>
            )}
          </Flex>

          <TextArea
            className="w-full resize-y"
            value={context.newValueTemplate || ''}
            onChange={handleNewValueTemplateChange}
            placeholder="Enter the new value template. Use {{variables}} or click the chips above to insert dynamic values."
            rows={6}
          />

          <Flex align="center" gap="3">
            <Button
              variant="solid"
              onClick={handlePreview}
              className="w-fit"
              disabled={isLoadingPreview || !taskId}
            >
              {isLoadingPreview ? (
                <>
                  <Spinner size="1" /> Loading...
                </>
              ) : (
                'Preview'
              )}
            </Button>
            {previewIsStale && context.stepId && effectiveFieldPath && (
              <Text size="1" color="orange">
                Preview required before running
              </Text>
            )}
          </Flex>

          {previewError && (
            <Callout.Root color="red" size="1">
              <Callout.Text>{previewError}</Callout.Text>
            </Callout.Root>
          )}
        </div>
      </div>

      {/* Preview change section */}
      {showPreview && previewTableData.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Heading size="3" weight="medium">
              Preview changes
            </Heading>
            <Text size="2" color="gray">
              Click a row to view the detailed diff for that task
            </Text>
          </div>

          {/* Preview table */}
          <DataTable.Root
            className="border rounded-lg"
            columns={previewTableColumns}
            data={previewTableData}
            enableRowSelection={true}
            enableMultiRowSelection={false}
            state={{ rowSelection: currentRowSelection }}
            onRowSelectionChange={handleRowSelectionChange}
            initialState={{
              pagination: {
                pageIndex: 0,
                pageSize: 5,
              },
            }}
          >
            {({ table }) => (
              <>
                <DataTable.Table>
                  <DataTable.Header>
                    {table.getHeaderGroups().map(headerGroup => (
                      <DataTable.HeaderGroup key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <DataTable.ColumnHeaderCell key={header.id} header={header} />
                        ))}
                      </DataTable.HeaderGroup>
                    ))}
                  </DataTable.Header>
                  <DataTable.Body>
                    {table.getRowModel().rows.map(row => (
                      <DataTable.Row
                        key={row.id}
                        row={row as any}
                        className={`cursor-pointer ${
                          row.getIsSelected()
                            ? 'bg-primary-50 hover:bg-primary-100'
                            : 'hover:bg-neutral-50'
                        }`}
                      >
                        {row.getVisibleCells().map(cell => (
                          <DataTable.Cell key={cell.id} cell={cell} />
                        ))}
                      </DataTable.Row>
                    ))}
                  </DataTable.Body>
                </DataTable.Table>
                {previewTableData.length > 5 && (
                  <DataTable.Pagination className="p-2 border-t justify-between">
                    <DataTable.PaginationTotal />
                    <DataTable.PaginationPageNavigation />
                  </DataTable.Pagination>
                )}
              </>
            )}
          </DataTable.Root>

          {/* Diff viewer for selected row */}
          {selectedRowData && selectedRowData.originalJson && selectedRowData.updatedJson && (
            <div className="flex flex-col gap-2">
              <Text size="2" weight="medium">
                Diff for task <Code>{selectedRowData.taskId}</Code>
              </Text>
              <div className="border rounded-lg overflow-hidden">
                <YamlDiffView
                  oldVersion={JSON.stringify(selectedRowData.originalJson, null, 2)}
                  newVersion={JSON.stringify(selectedRowData.updatedJson, null, 2)}
                  leftTitle="Original Response"
                  rightTitle="Updated Response"
                />
              </div>
            </div>
          )}

          {/* Show error if selected row has an error */}
          {selectedRowData && selectedRowData.error && (
            <Callout.Root color="red" size="1">
              <Callout.Text>
                Error for task {selectedRowData.taskId}: {selectedRowData.error}
              </Callout.Text>
            </Callout.Root>
          )}
        </div>
      )}
    </div>
  );
};

ResponseBackfillCardComponent.displayName = 'ResponseBackfillCardComponent';

export default ResponseBackfillCardComponent;
